import React, { useCallback, useMemo, useState, FC } from 'react'
import makeStyles from '@mui/styles/makeStyles';

import prettyBytes from 'pretty-bytes'

import { useSelector, useDispatch } from 'react-redux'
import { useDropzone } from 'react-dropzone'

import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Box from '@mui/material/Box'

import HelperText from '@kaidam/fc-shared/src/frontend/components/form/fields/HelperText'

import TinyButton from '../widgets/TinyButton'
import ClickLink from '../widgets/ClickLink'
import SmallText from '../widgets/SmallText'
import UploadedImage from '../widgets/UploadedImage'
import UploadedVideo from '../widgets/UploadedVideo'
import useFileUrl from '../hooks/useFileUrl'

import UploadStatus from '../uploader/UploadStatus'
import FileChooser, { IFileChooserFileType } from '../filestore/FileChooser'

import fileuploadActions from '../../store/modules/fileupload'
import fileuploadSelectors from '../../store/selectors/fileupload'

import icons from '../../icons'

import {
  ThunkDispatcher,
} from '../../types/store'

import {
  IField_Storage_File,
  IField_Storage_Location,
} from '@kaidam/fc-shared/src/types/storage'
import { color } from '@mui/system';

const DeleteIcon = icons.delete
const UploadIcon = icons.upload
const SearchIcon = icons.search

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1),
  },
  info: {
    display: 'flex',
    flexDirection: 'row',
  },
  intro: {
    marginRight: theme.spacing(2),
  },
  imageContainer: {
    marginTop: theme.spacing(2),
  },
  filepath: {
    //marginTop: theme.spacing(2),
  }
}))

const FileInput: FC<React.PropsWithChildren<{
  value: IField_Storage_File | null,
  location?: IField_Storage_Location,
  uploadActive?: boolean,
  chooserActive?: boolean,
  displayTypes?: IFileChooserFileType[],
  selectTypes?: IFileChooserFileType[],
  accept?: string | string[],
  readonly?: boolean,
  noReset?: boolean,
  publicMode?: boolean,
  sharedMode?: boolean,
  title?: string,
  helperText?: string,
  useTextButtons?: boolean,
  onChange: {
    (file: IField_Storage_File | null): void,
  }
}>> = ({
  value,
  location,
  uploadActive = true,
  chooserActive = false,
  displayTypes,
  selectTypes,
  accept,
  noReset,
  publicMode,
  sharedMode,
  readonly,
  title,
  helperText,
  useTextButtons,
  onChange,
}) => {
  
  const classes = useStyles()
  const dispatch = useDispatch()
  const thunkDispatch = dispatch as ThunkDispatcher
  
  const [ choosing, setChoosing ] = useState(false)
  const uploadInProgress = useSelector(fileuploadSelectors.inProgress)
  const uploadStatus = useSelector(fileuploadSelectors.status)
  const uploadError = useSelector(fileuploadSelectors.network.uploadFiles.error)

  const fileUrl = useFileUrl(value)

  const {
    getRootProps,
    getInputProps,
    inputRef,
  } = useDropzone({
    accept,
    onDrop: async (files) => {
      if(readonly) return
      if(!location) return
      if(files.length <= 0) return
      const result = await thunkDispatch(fileuploadActions.uploadFiles({
        location,
        files,
      }))
      if(!result || !result[0]) return
      onChange(result[0])
    },
    multiple: false,
  })

  const onChooseFile = useCallback((file: IField_Storage_File) => {
    onChange(file)
    setChoosing(false)
  }, [
    onChange,
  ])

  const onOpenUploader = useCallback(() => {
    if(!inputRef.current) return
    inputRef.current.click()
  }, [])

  const onResetValue = useCallback(() => {
    onChange(null)
  }, [])

  const buttons = useMemo(() => {

    if(readonly) return []

    const buttonItems = []
    
    if(uploadActive) {
      buttonItems.push({
        title: 'Upload file',
        help: 'Upload a file from your computer',
        handler: onOpenUploader,
      })
    }

    if(chooserActive) {
      buttonItems.push({
        title: 'Find in Filestore',
        help: 'Find a file in the filestore',
        handler: () => setChoosing(true),
      })      
    }

    if(!noReset) {
      buttonItems.push({
        title: 'Reset Upload',
        help: 'Clear this file',
        handler: onResetValue,
        color: 'secondary',
      })
    }
    
    return buttonItems
      .map((button, i) => {
        if(useTextButtons) {
          return (
            <ClickLink
              key={ i }
              textDecoration
              onClick={ button.handler }
            >
              <SmallText color="#0000ff">
                { button.title }
              </SmallText>
            </ClickLink>
          )
        } else {
          return (
            <TinyButton
              key={ i }
              variant="outlined"
              size="small"
              onClick={ button.handler }
            >
              { button.title }
            </TinyButton>
          )
        }
      })
  }, [
    chooserActive,
    readonly,
    onResetValue,
    noReset,
    classes,
  ])

  return uploadInProgress ? (
    <Box
      className="container"
      sx={{
        mt: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <UploadStatus
            error={ uploadError }
            status={ uploadStatus }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <>
      <Box
        className="container"
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
        }}
      >
        <Box
          className="cell"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <div className={ classes.intro }>
            <InputLabel>{ title }
            </InputLabel>
          </div>
          { buttons }
        </Box>
        <Box
          className="cell"
          sx={{
            flexGrow: 1,
            p: 4,
          }}
        >
          {
            value ? (
              <div className={ classes.intro }>
                {
                  value && value.meta && (
                    <>
                      <FormHelperText error={ false }>
                        {
                          fileUrl ? (
                            <a href={ fileUrl } target="_blank">{ value.meta.filename }</a>
                          ) : (
                            value.meta.filename
                          )
                        }
                      </FormHelperText>
                      <FormHelperText error={ false }>
                        { prettyBytes(parseInt(value.meta.size ? value.meta.size : '0')) }
                      </FormHelperText>
                    </>
                  )
                }
                <UploadedImage
                  file={ value }
                />
                <UploadedVideo
                  file={ value }
                />
              </div>
            ) : (
              <div className={ classes.intro }>
                {
                  helperText && !readonly ? (
                    <FormHelperText error={ false }>
                      { helperText }
                    </FormHelperText>
                  ) : null
                }
              </div>
            )
          }
        </Box>
      </Box>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
      </div>
      {
        uploadError && (
          <HelperText 
            helperText={ uploadError }
            error
            touched
          />
        )
      }
      {
        choosing && (
          <FileChooser
            publicMode={ publicMode }
            sharedMode={ sharedMode }
            displayTypes={ displayTypes }
            selectTypes={ selectTypes }
            onChoose={ onChooseFile }
            onClose={ () => setChoosing(false) }
          />
        )
      }
    </>
  )
}

export default FileInput
